// NGODashboard (Task 13.4).
//
// Composes the NGO dashboard: Overview counts, Available Donations with search
// and Food Category / Restaurant / Meal Count / Available-today filters, My
// Claims, History, Impact, and recent achievements. Each section loads
// independently with error isolation (Req 21.7) and shows an empty state when
// it has no records (Req 21.8). Search/filter logic delegates to the shared
// `evaluateSearch` domain function (Req 22.1, 22.2, 22.6).

import { useMemo, useState } from "react";
import {
  FOOD_CATEGORIES,
  SEARCH_TERM_REQUIRED,
  evaluateSearch,
  recentAchievements,
  type Achievement,
  type Donation,
  type FoodCategory,
} from "@sharebite/domain";
import { AsyncSection } from "../../components/AsyncSection";
import { DonationRow } from "../../components/DonationCard";
import { Skeleton } from "../../components/UIStateWrapper";
import { Card, Stat } from "../../components/primitives";
import { useAsyncData } from "../../hooks/useAsyncData";
import { badgeName } from "../../lib/badgeDisplay";
import * as api from "../../lib/api";

const ACTIVE_CLAIM_STATUSES = new Set(["Reserved", "Collected"]);
const HISTORY_CLAIM_STATUSES = new Set(["Completed", "Cancelled"]);

function isToday(epochMs: number): boolean {
  const d = new Date(epochMs);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function AvailableDonations({ onReserved }: { onReserved: () => void }): JSX.Element {
  const { state, reload } = useAsyncData<Donation[]>(() =>
    api.listAvailableDonations(),
  );

  const [queryInput, setQueryInput] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [category, setCategory] = useState<FoodCategory | "">("");
  const [restaurant, setRestaurant] = useState("");
  const [minMeals, setMinMeals] = useState("");
  const [availableToday, setAvailableToday] = useState(false);

  const all = state.status === "success" ? state.data : [];

  const restaurants = useMemo(
    () => Array.from(new Set(all.map((d) => d.restaurantUid))),
    [all],
  );

  const filtered = useMemo(() => {
    let items = all;
    // Conjunctive filters (Req 22.2).
    if (category) items = items.filter((d) => d.foodCategory === category);
    if (restaurant) items = items.filter((d) => d.restaurantUid === restaurant);
    if (minMeals) {
      const min = Number(minMeals);
      if (Number.isFinite(min)) items = items.filter((d) => d.mealCount >= min);
    }
    if (availableToday) items = items.filter((d) => isToday(d.pickupDeadline));

    if (submittedQuery !== null && submittedQuery.length > 0) {
      const result = evaluateSearch({
        items,
        query: submittedQuery,
        getSearchText: (d) =>
          `${d.foodName} ${d.description} ${d.specialInstructions}`,
      });
      if (result.accepted) return result.results;
    }
    return items;
  }, [all, category, restaurant, minMeals, availableToday, submittedQuery]);

  function handleSearch(e: React.FormEvent): void {
    e.preventDefault();
    if (queryInput.length === 0) {
      // Reject a zero-character query (Req 22.6).
      setSearchError(SEARCH_TERM_REQUIRED);
      setSubmittedQuery(null);
      return;
    }
    setSearchError(null);
    setSubmittedQuery(queryInput);
  }

  async function reserve(donationId: string): Promise<void> {
    await api.reserveDonation({ donationId });
    onReserved();
    reload();
  }

  return (
    <Card title="Available donations">
      <form className="sb-row" onSubmit={handleSearch} style={{ marginBottom: 12 }}>
        <input
          className="sb-input"
          placeholder="Search donations"
          aria-label="Search donations"
          value={queryInput}
          onChange={(e) => setQueryInput(e.target.value)}
          style={{ flex: "2 1 160px" }}
        />
        <button type="submit" className="sb-btn">
          Search
        </button>
      </form>
      {searchError && <p className="sb-field__error">A search term is required.</p>}

      <div className="sb-row" style={{ marginBottom: 12 }}>
        <select
          className="sb-select"
          aria-label="Food Category filter"
          value={category}
          onChange={(e) => setCategory(e.target.value as FoodCategory | "")}
          style={{ flex: "1 1 120px" }}
        >
          <option value="">All categories</option>
          {FOOD_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          className="sb-select"
          aria-label="Restaurant filter"
          value={restaurant}
          onChange={(e) => setRestaurant(e.target.value)}
          style={{ flex: "1 1 120px" }}
        >
          <option value="">All restaurants</option>
          {restaurants.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <input
          className="sb-input"
          aria-label="Minimum meal count filter"
          inputMode="numeric"
          placeholder="Min meals"
          value={minMeals}
          onChange={(e) => setMinMeals(e.target.value)}
          style={{ flex: "1 1 90px" }}
        />
        <label className="sb-pill" style={{ cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={availableToday}
            onChange={(e) => setAvailableToday(e.target.checked)}
          />{" "}
          Available today
        </label>
      </div>

      {state.status === "loading" && <Skeleton rows={3} />}
      {state.status === "error" && (
        <div className="sb-error" role="alert">
          <p>Couldn't load this. Please try again.</p>
          <button type="button" className="sb-btn sb-btn--secondary" onClick={reload}>
            Retry
          </button>
        </div>
      )}
      {state.status === "success" &&
        (all.length === 0 ? (
          // No available donations at all (Req 29.3).
          <div className="sb-empty" data-testid="empty-state">
            No donations available.
          </div>
        ) : filtered.length === 0 ? (
          // Search/filter produced no matches; query and filters are retained
          // (Req 22.5).
          <div className="sb-empty">No results found.</div>
        ) : (
          filtered.map((d) => (
            <DonationRow
              key={d.donationId}
              donation={d}
              actions={
                <button
                  type="button"
                  className="sb-btn"
                  onClick={() => void reserve(d.donationId)}
                >
                  Reserve
                </button>
              }
            />
          ))
        ))}
    </Card>
  );
}

export function NGODashboard({ uid }: { uid: string }): JSX.Element {
  const [claimsNonce, setClaimsNonce] = useState(0);

  return (
    <div className="sb-main">
      <h1>NGO dashboard</h1>

      <AsyncSection
        title="Overview"
        loader={() => api.ngoOverview()}
        emptyText=""
      >
        {(counts) => (
          <div className="sb-grid">
            <Stat label="Meals Received" value={counts.mealsReceived} />
            <Stat label="Active Claims" value={counts.activeClaims} />
            <Stat label="Completed Claims" value={counts.completedClaims} />
          </div>
        )}
      </AsyncSection>

      <AvailableDonations onReserved={() => setClaimsNonce((n) => n + 1)} />

      {/* My claims --------------------------------------------------------- */}
      <AsyncSection
        key={`claims-${claimsNonce}`}
        title="My claims"
        loader={() => api.listDonationsByReserver(uid)}
        emptyText="No active claims."
        isEmpty={(ds: Donation[]) =>
          ds.filter((d) => ACTIVE_CLAIM_STATUSES.has(d.status)).length === 0
        }
      >
        {(ds: Donation[]) => (
          <div>
            {ds
              .filter((d) => ACTIVE_CLAIM_STATUSES.has(d.status))
              .map((d) => (
                <ClaimRow key={d.donationId} donation={d} />
              ))}
          </div>
        )}
      </AsyncSection>

      {/* History ----------------------------------------------------------- */}
      <AsyncSection
        title="History"
        loader={() => api.listDonationsByReserver(uid)}
        emptyText="No past claims."
        isEmpty={(ds: Donation[]) =>
          ds.filter((d) => HISTORY_CLAIM_STATUSES.has(d.status)).length === 0
        }
      >
        {(ds: Donation[]) => (
          <div>
            {ds
              .filter((d) => HISTORY_CLAIM_STATUSES.has(d.status))
              .sort((a, b) => b.createdAt - a.createdAt)
              .map((d) => (
                <DonationRow key={d.donationId} donation={d} />
              ))}
          </div>
        )}
      </AsyncSection>

      {/* Impact ------------------------------------------------------------ */}
      <AsyncSection title="Impact" loader={() => api.ngoOverview()} emptyText="">
        {(counts) => (
          <div className="sb-grid sb-grid--2">
            <Stat label="Total Meals Received" value={counts.mealsReceived} />
            <Stat label="Completed Claims" value={counts.completedClaims} />
          </div>
        )}
      </AsyncSection>

      {/* Recent achievements ----------------------------------------------- */}
      <AsyncSection
        title="Recent achievements"
        loader={() => api.listAchievements(uid)}
        emptyText="No achievements earned yet."
        isEmpty={(a: Achievement[]) => a.length === 0}
      >
        {(achievements: Achievement[]) => (
          <div>
            {recentAchievements(achievements).map((a) => (
              <span key={a.achievementId} className="sb-badge">
                {badgeName(a.badgeId)}
              </span>
            ))}
          </div>
        )}
      </AsyncSection>
    </div>
  );
}

function ClaimRow({
  donation,
}: {
  donation: Donation;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function act(fn: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    try {
      await fn();
      setDone(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <DonationRow
      donation={donation}
      actions={
        done ? (
          <span className="sb-muted">Updated. Refresh to see changes.</span>
        ) : (
          <>
            {donation.status === "Reserved" && (
              <button
                type="button"
                className="sb-btn"
                disabled={busy}
                onClick={() =>
                  void act(() =>
                    api.markCollected({ donationId: donation.donationId }),
                  )
                }
              >
                Mark collected
              </button>
            )}
            {donation.status === "Reserved" && (
              <button
                type="button"
                className="sb-btn sb-btn--secondary"
                disabled={busy}
                onClick={() =>
                  void act(() =>
                    api.cancelReservation({ donationId: donation.donationId }),
                  )
                }
              >
                Cancel reservation
              </button>
            )}
            <span className="sb-spacer" />
            <span className="sb-muted">Restaurant: {donation.restaurantUid}</span>
          </>
        )
      }
    />
  );
}
